import React, { useState } from 'react';
import { Search, ArrowRight, Bookmark, Clock, Share2, Sparkles, ExternalLink } from 'lucide-react';
import { ClarvosCTAButton, ClarvosPillBadge } from './ClarvosBrand';

export const TheFeedPage: React.FC<{ onRequestDemo: () => void }> = ({ onRequestDemo }) => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  const categories = ['All', 'AAC & Speech', 'Neuro-Rehab', 'Pediatric AI', 'School Case Studies'];

  const articles = [
    {
      id: 1,
      category: 'AAC & Speech',
      title: 'Real-Time Neural Acoustic Adaptation in Non-Verbal Autism Communication',
      snippet: 'How sub-50ms synthesis loops and dynamic symbol grouping reduce communication fatigue by up to 64% in early childhood therapy.',
      date: 'September 2026',
      readTime: '6 min read',
      author: 'Dr. Elena Rostova',
      tag: 'Clinical Research',
      tagColor: 'bg-clarvos-yellow text-clarvos-black',
      image: 'https://images.unsplash.com/photo-1576765608535-5f04d1e3f289?w=600&h=400&fit=crop',
    },
    {
      id: 2,
      category: 'Neuro-Rehab',
      title: 'Predictive Motor Dwell Calibration: Eliminating Wheelchair Switch Latency',
      snippet: 'Evaluating adaptive exponential dwell timers for users with Cerebral Palsy and ALS across 250 longitudinal clinic days.',
      date: 'August 2026',
      readTime: '8 min read',
      author: 'Marcus Vance',
      tag: 'Biomechanics',
      tagColor: 'bg-clarvos-mint text-clarvos-black',
      image: 'https://images.unsplash.com/photo-1584515979956-d9f6e5d09982?w=600&h=400&fit=crop',
    },
    {
      id: 3,
      category: 'School Case Studies',
      title: 'How 140 Public School Districts Unlocked Multi-Sensory IEP Compliance in Under 30 Days',
      snippet: 'A comprehensive study on transitioning from siloed desktop software to unified mobile AbilityOS workstations.',
      date: 'July 2026',
      readTime: '5 min read',
      author: 'Priya Sundaram, Ed.D',
      tag: 'District Whitepaper',
      tagColor: 'bg-clarvos-violet text-clarvos-black',
      image: 'https://images.unsplash.com/photo-1509062522246-3755977927d7?w=600&h=400&fit=crop',
    },
    {
      id: 4,
      category: 'Pediatric AI',
      title: 'Ethical Guardrails in Agentic Therapy: Keeping Clinicians at the Core',
      snippet: 'Why autonomous agentic workflows must employ non-bypassable human verification gates when altering student treatment protocols.',
      date: 'June 2026',
      readTime: '10 min read',
      author: 'AbilityOS Ethics Council',
      tag: 'Policy & Safety',
      tagColor: 'bg-clarvos-pink text-clarvos-black',
      image: 'https://images.unsplash.com/photo-1532938911079-1b06ac7ceec7?w=600&h=400&fit=crop',
    },
  ];

  const filteredArticles = articles.filter(a => {
    const matchesCat = selectedCategory === 'All' || a.category === selectedCategory;
    const matchesSearch = a.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          a.snippet.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCat && matchesSearch;
  });

  return (
    <div className="bg-clarvos-cream min-h-screen text-clarvos-black pb-24">
      {/* 1. Header Banner - Solar Yellow */}
      <section className="bg-clarvos-yellow pt-32 pb-20 px-6 sm:px-12 rounded-b-[40px] text-center relative overflow-hidden">
        <div className="max-w-4xl mx-auto">
          <ClarvosPillBadge label="Clinical Intelligence" variant="dark" className="mb-4" />
          <h1 className="text-4xl sm:text-6xl font-extrabold tracking-tight mb-4">
            The AbilityOS Feed
          </h1>
          <p className="text-lg sm:text-xl text-clarvos-charcoal max-w-2xl mx-auto leading-relaxed">
            Latest clinical breakthroughs, biomechanical studies, assistive technology research, and special education field dispatches.
          </p>
        </div>
      </section>

      {/* 2. Filters & Search Bar */}
      <section className="py-12 px-6 sm:px-12 max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6 pb-8 border-b border-clarvos-gray-300">
          {/* Category Pills */}
          <div className="flex flex-wrap items-center gap-2">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-5 py-2.5 rounded-full font-bold text-xs tracking-wider transition-all duration-300 ${
                  selectedCategory === cat
                    ? 'bg-clarvos-black text-white shadow-md'
                    : 'bg-white border border-clarvos-gray-300 text-clarvos-black hover:border-clarvos-black'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Search Box */}
          <div className="relative w-full md:w-80">
            <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-clarvos-gray-600" />
            <input
              type="text"
              placeholder="Search research & clinical articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-white border border-clarvos-gray-300 rounded-full pl-11 pr-4 py-2.5 text-sm focus:outline-none focus:border-clarvos-black focus:ring-2 focus:ring-clarvos-yellow/40 transition-all"
            />
          </div>
        </div>

        {/* 3. Articles Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-12">
          {filteredArticles.map((article) => (
            <div
              key={article.id}
              className="bg-white rounded-[32px] border border-clarvos-gray-300 overflow-hidden shadow-sm hover:shadow-xl hover:border-clarvos-black transition-all duration-500 flex flex-col group"
            >
              <div className="h-64 overflow-hidden relative">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute top-4 left-4">
                  <span className={`px-3.5 py-1 rounded-full text-xs font-extrabold uppercase tracking-wider ${article.tagColor}`}>
                    {article.tag}
                  </span>
                </div>
              </div>

              <div className="p-8 sm:p-10 flex flex-col flex-1 justify-between">
                <div>
                  <div className="flex items-center gap-3 text-xs text-clarvos-gray-600 mb-3 font-medium">
                    <span>{article.date}</span>
                    <span>•</span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" />
                      {article.readTime}
                    </span>
                    <span>•</span>
                    <span>{article.author}</span>
                  </div>

                  <h3 className="text-2xl font-bold tracking-tight mb-3 group-hover:text-clarvos-charcoal transition-colors">
                    {article.title}
                  </h3>

                  <p className="text-clarvos-gray-600 text-sm leading-relaxed mb-6">
                    {article.snippet}
                  </p>
                </div>

                <div className="pt-6 border-t border-clarvos-gray-300/60 flex items-center justify-between">
                  <span className="font-bold text-xs uppercase tracking-wider text-clarvos-black group-hover:underline flex items-center gap-1">
                    Read Full Paper
                    <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                  <div className="flex items-center gap-2">
                    <button className="w-9 h-9 rounded-full bg-clarvos-cream border border-clarvos-gray-300 flex items-center justify-center hover:bg-clarvos-yellow transition-colors">
                      <Share2 className="w-4 h-4 text-clarvos-black" />
                    </button>
                    <button className="w-9 h-9 rounded-full bg-clarvos-cream border border-clarvos-gray-300 flex items-center justify-center hover:bg-clarvos-yellow transition-colors">
                      <Bookmark className="w-4 h-4 text-clarvos-black" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredArticles.length === 0 && (
          <div className="text-center py-20 bg-white rounded-[32px] border border-clarvos-gray-300">
            <h4 className="text-xl font-bold mb-2">No articles matched your search</h4>
            <p className="text-sm text-clarvos-gray-600">Try adjusting your keyword or selecting "All" categories.</p>
          </div>
        )}
      </section>

      {/* 4. Bottom Subscribe Section */}
      <section className="max-w-5xl mx-auto px-6 mt-16">
        <div className="bg-clarvos-black text-white p-10 sm:p-14 rounded-[36px] text-center relative overflow-hidden">
          <ClarvosPillBadge label="Stay Informed" variant="yellow" className="mb-4" />
          <h3 className="text-2xl sm:text-4xl font-extrabold mb-4">
            Receive Clinical Updates Directly to Your Inbox
          </h3>
          <p className="text-clarvos-gray-300 text-sm sm:text-base max-w-xl mx-auto mb-8">
            Monthly digests curated for clinical directors, occupational therapists, and assistive technology coordinators.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 max-w-md mx-auto">
            <input
              type="email"
              placeholder="Enter your work email address"
              className="w-full sm:flex-1 bg-clarvos-charcoal border border-clarvos-gray-600 rounded-full px-5 py-3 text-sm text-white placeholder-clarvos-gray-300 focus:outline-none focus:border-clarvos-yellow"
            />
            <ClarvosCTAButton
              text="Subscribe"
              hoverText="Join"
              variant="yellow"
              size="md"
              className="w-full sm:w-auto"
            />
          </div>
        </div>
      </section>
    </div>
  );
};
